import * as React from 'react';
import { cn } from '@/lib/utils';
import { CDEditableText } from '../primitives/CDEditableText';
import { CDSectionHeader } from '../primitives/CDSectionHeader';

export interface SignatureLine {
  name?: string;
  signature?: string;
  date?: string;
}

export interface ConfirmReceiptData {
  applicants: SignatureLine[];
}

export interface CDConfirmReceiptProps {
  /** Confirm receipt data */
  data: Partial<ConfirmReceiptData>;
  /** Whether fields are editable */
  editable?: boolean;
  /** onChange handler */
  onChange?: (data: Partial<ConfirmReceiptData>) => void;
  /** Additional className */
  className?: string;
}

/**
 * CDConfirmReceipt - Confirm Receipt signature section on Page 5
 *
 * Contains signature lines for all borrowers/applicants.
 */
export const CDConfirmReceipt = React.forwardRef<HTMLDivElement, CDConfirmReceiptProps>(
  ({ data, editable = false, onChange, className }, ref) => {
    const applicants = data.applicants || [{}];

    // Helper to update an applicant's field
    const updateApplicant = (index: number, field: keyof SignatureLine, value: string) => {
      const updatedApplicants = [...applicants];
      // Ensure we have enough entries
      while (updatedApplicants.length <= index) {
        updatedApplicants.push({});
      }
      updatedApplicants[index] = { ...updatedApplicants[index], [field]: value };
      onChange?.({ ...data, applicants: updatedApplicants });
    };

    return (
      <div ref={ref} className={cn('', className)}>
        <CDSectionHeader title="Confirm Receipt" />

        <div className="border border-t-0 border-cd-black p-2">
          <div className="cd-text-tiny mb-3">
            By signing, you are only confirming that you have received this form. You do not have to
            accept this loan because you have signed or received this form.
          </div>

          <div className="grid grid-cols-4 gap-3">
            {/* First Applicant Signature */}
            <div>
              <div className="border-b border-cd-black h-6 mb-0.5">
                <CDEditableText
                  value={applicants[0]?.signature || ''}
                  editable={editable}
                  onChange={(v) => updateApplicant(0, 'signature', v)}
                  size="small"
                  placeholder="Sign here"
                />
              </div>
              <div className="cd-text-tiny">Applicant Signature</div>
            </div>

            {/* First Applicant Date */}
            <div>
              <div className="border-b border-cd-black h-6 mb-0.5">
                <CDEditableText
                  value={applicants[0]?.date || ''}
                  editable={editable}
                  onChange={(v) => updateApplicant(0, 'date', v)}
                  size="small"
                  placeholder="MM/DD/YYYY"
                />
              </div>
              <div className="cd-text-tiny">Date</div>
            </div>

            {/* Co-Applicant Signature */}
            <div>
              <div className="border-b border-cd-black h-6 mb-0.5">
                <CDEditableText
                  value={applicants[1]?.signature || ''}
                  editable={editable}
                  onChange={(v) => updateApplicant(1, 'signature', v)}
                  size="small"
                  placeholder="Sign here"
                />
              </div>
              <div className="cd-text-tiny">Co-Applicant Signature</div>
            </div>

            {/* Co-Applicant Date */}
            <div>
              <div className="border-b border-cd-black h-6 mb-0.5">
                <CDEditableText
                  value={applicants[1]?.date || ''}
                  editable={editable}
                  onChange={(v) => updateApplicant(1, 'date', v)}
                  size="small"
                  placeholder="MM/DD/YYYY"
                />
              </div>
              <div className="cd-text-tiny">Date</div>
            </div>
          </div>
        </div>
      </div>
    );
  },
);

CDConfirmReceipt.displayName = 'CDConfirmReceipt';
